import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import InputLabel from '../../../../../components/v2/InputLabel';
import { FileCategory, getFileCategory } from '../../../../../utils/file';
import useToast from '../../../../hooks/useToast';
import { StyledButton } from '../../../../components/Templates/components/TemplateView/styles';
import {
  HeroImageContainer,
  HeroImageDescription,
  HeroImageDescriptionContainer,
  HeroImageTipsDescription,
} from '../../../Config/components/CheckoutPageConfiguration/HeroImageSetting/styles';

import { MediaActions, MediaFrame } from './styles';
import texts from './texts.json';

const MAX_FILE_SIZE_MB = 250;
const MAX_FILE_UPLOAD_SIZE = MAX_FILE_SIZE_MB * 1000000;

/**
 * The one video or image beside the lead form's end screen, previewed in the 16:9 frame leads see.
 * The picked file uploads on Save Changes.
 */
const EndScreenMediaSetting = ({ media, onMediaChange, isReadOnly }) => {
  const { showToast } = useToast();

  const replaceMedia = (nextMedia) => {
    if (media?.file) {
      URL.revokeObjectURL(media.url);
    }
    onMediaChange(nextMedia);
  };

  const onFileSelected = ({ target }) => {
    const [file] = target.files;
    // eslint-disable-next-line no-param-reassign
    target.value = '';
    if (!file) {
      return;
    }
    const category = getFileCategory(file);
    if (category !== FileCategory.IMAGE && category !== FileCategory.VIDEO) {
      showToast(texts.errors.mediaType, { error: true });
      return;
    }
    if (file.size > MAX_FILE_UPLOAD_SIZE) {
      showToast(format(texts.errors.fileSize, { maxFileSize: MAX_FILE_SIZE_MB }), { error: true });
      return;
    }
    replaceMedia({ url: URL.createObjectURL(file), category, file });
  };

  return (
    <>
      <InputLabel>{texts.endScreenMedia}</InputLabel>
      <HeroImageContainer>
        <MediaActions>
          {!!media && (
            <MediaFrame>
              {media.category === FileCategory.VIDEO
                ? <video src={media.url} controls playsInline aria-label={texts.endScreenMedia} />
                : <img src={media.url} alt={texts.endScreenMedia} />}
            </MediaFrame>
          )}
          <StyledButton variant="contained" component="label" disabled={isReadOnly}>
            {media ? texts.changeMedia : texts.addMedia}
            <input hidden type="file" accept="image/*,video/*" onChange={onFileSelected} />
          </StyledButton>
          {!!media && (
            <StyledButton variant="contained" disabled={isReadOnly} onClick={() => replaceMedia(null)}>
              {texts.removeMedia}
            </StyledButton>
          )}
        </MediaActions>
        <HeroImageDescriptionContainer>
          <HeroImageDescription>{texts.mediaDescription}</HeroImageDescription>
          <HeroImageTipsDescription>{texts.mediaTips}</HeroImageTipsDescription>
          <ul>
            {texts.mediaRequirements.map((text) => (
              <li key={text}>{format(text, { maxFileSize: MAX_FILE_SIZE_MB })}</li>
            ))}
          </ul>
        </HeroImageDescriptionContainer>
      </HeroImageContainer>
    </>
  );
};

EndScreenMediaSetting.propTypes = {
  media: PropTypes.shape({
    url: PropTypes.string,
    category: PropTypes.string,
    file: PropTypes.object,
  }),
  onMediaChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
};

EndScreenMediaSetting.defaultProps = {
  media: null,
  isReadOnly: false,
};

export default EndScreenMediaSetting;
